/**
 * WorkflowDashboard Component
 * Shows overview stats, upcoming runs, and recent activity for workflows
 */

import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { scheduledWorkflowsService } from '../../services/scheduled-workflows.service';
import type { ScheduledWorkflow, WorkflowExecution } from '../../services/scheduled-workflows.service';
import { useScopeContext } from '../../contexts/ScopeContext';
import { WorkflowStatusBadge } from './WorkflowStatusBadge';

interface WorkflowDashboardProps {
  agentId: string;
  onCreateWorkflow?: () => void;
}

interface ExecutionWithWorkflow extends WorkflowExecution {
  workflowName: string;
}

export function WorkflowDashboard({ agentId, onCreateWorkflow }: WorkflowDashboardProps) {
  const scope = useScopeContext();
  const [workflows, setWorkflows] = useState<ScheduledWorkflow[]>([]);
  const [recentExecutions, setRecentExecutions] = useState<ExecutionWithWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, [agentId, scope.teamId]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all workflows for this agent
      const allWorkflows = await scheduledWorkflowsService.listWorkflows(scope.teamId);
      const agentWorkflows = allWorkflows.filter(w => w.agentId === agentId);
      setWorkflows(agentWorkflows);

      // Fetch recent executions from each workflow (limit 3 per workflow, 10 total)
      const executionPromises = agentWorkflows.slice(0, 5).map(async (workflow) => {
        try {
          const executions = await scheduledWorkflowsService.getWorkflowHistory(
            scope.teamId,
            workflow.workflowId,
            3
          );
          return executions.map(e => ({ ...e, workflowName: workflow.name }));
        } catch {
          return [];
        }
      });

      const allExecutions = (await Promise.all(executionPromises)).flat();
      // Sort by executedAt or scheduledFor descending and take top 10
      const sorted = allExecutions
        .sort((a, b) => {
          const dateA = new Date(a.executedAt || a.scheduledFor).getTime();
          const dateB = new Date(b.executedAt || b.scheduledFor).getTime();
          return dateB - dateA;
        })
        .slice(0, 10);
      setRecentExecutions(sorted);

    } catch (err) {
      console.error('[WorkflowDashboard] Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowClick = (workflowId: string) => {
    route(`${scope.getNavigationPrefix()}/workflows/${agentId}/${workflowId}`);
  };

  const handleRunNow = async (workflowId: string, e: Event) => {
    e.stopPropagation();
    try {
      const result = await scheduledWorkflowsService.runWorkflow(scope.teamId, workflowId);
      route(`${scope.getNavigationPrefix()}/workflows/${agentId}/${workflowId}/${result.threadId}`);
    } catch (err) {
      console.error('Failed to run workflow:', err);
    }
  };

  // Stats
  const activeCount = workflows.filter(w => w.status === 'active').length;
  const completedCount = recentExecutions.filter(e => e.status === 'completed').length;
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const runsToday = recentExecutions.filter(e => {
    const execDate = new Date(e.executedAt || e.scheduledFor);
    return execDate >= todayStart;
  }).length;
  // Total runs - sum from workflow records (will populate as workflows run)
  const totalRuns = workflows.reduce((sum, w) => sum + ((w as any).runCount || 0), 0);

  // Upcoming runs (sorted by nextRun)
  const upcomingRuns = workflows
    .filter(w => w.status === 'active' && w.nextRun)
    .sort((a, b) => new Date(a.nextRun!).getTime() - new Date(b.nextRun!).getTime())
    .slice(0, 5);

  const formatRelativeTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMs < 0) {
      // Past
      const absMins = Math.abs(diffMins);
      const absHours = Math.abs(diffHours);
      const absDays = Math.abs(diffDays);
      if (absMins < 60) return `${absMins}m ago`;
      if (absHours < 24) return `${absHours}h ago`;
      if (absDays < 7) return `${absDays}d ago`;
      return date.toLocaleDateString();
    }
    // Future
    if (diffMins < 60) return `in ${diffMins}m`;
    if (diffHours < 24) return `in ${diffHours}h`;
    if (diffDays < 7) return `in ${diffDays}d`;
    return date.toLocaleDateString();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✓';
      case 'failed': return '✗';
      case 'running': return '●';
      case 'pending': return '○';
      default: return '○';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'failed': return 'text-error';
      case 'running': return 'text-warning';
      case 'pending': return 'text-base-content/50';
      default: return 'text-base-content/50';
    }
  };

  if (loading) {
    return (
      <div class="flex-1 flex items-center justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  // Empty state when no workflows exist
  if (workflows.length === 0) {
    return (
      <div class="flex-1 overflow-y-auto px-4 py-6">
        <div class="hero min-h-[40vh]">
          <div class="hero-content text-center">
            <div class="max-w-md">
              <h1 class="text-3xl font-bold">Scheduled Workflows</h1>
              <p class="py-6">
                Create workflows to automate recurring tasks. Click "+ New Workflow" to get started.
              </p>
              {onCreateWorkflow && (
                <button class="btn btn-primary" onClick={onCreateWorkflow}>
                  + New Workflow
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="flex-1 overflow-y-auto px-6 py-6">
      <div class="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div class="flex items-center justify-between">
          <h1 class="text-2xl font-bold">Workflow Dashboard</h1>
          {onCreateWorkflow && (
            <button class="btn btn-primary btn-sm" onClick={onCreateWorkflow}>
              + New Workflow
            </button>
          )}
        </div>

        {/* Stats Cards */}
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div class="stat bg-base-100 border border-base-300 rounded-lg px-4 py-3">
            <div class="stat-title text-xs">Active</div>
            <div class="stat-value text-2xl text-success">{activeCount}</div>
          </div>
          <div class="stat bg-base-100 border border-base-300 rounded-lg px-4 py-3">
            <div class="stat-title text-xs">Completed</div>
            <div class="stat-value text-2xl text-info">{completedCount}</div>
          </div>
          <div class="stat bg-base-100 border border-base-300 rounded-lg px-4 py-3">
            <div class="stat-title text-xs">Runs Today</div>
            <div class="stat-value text-2xl">{runsToday}</div>
          </div>
          <div class="stat bg-base-100 border border-base-300 rounded-lg px-4 py-3">
            <div class="stat-title text-xs">Total Runs</div>
            <div class="stat-value text-2xl">{totalRuns}</div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Runs */}
          <div class="card bg-base-100 border border-base-300">
            <div class="card-body p-4">
              <h2 class="card-title text-base">Upcoming Runs</h2>
              {upcomingRuns.length === 0 ? (
                <p class="text-base-content/60 text-sm">No scheduled runs</p>
              ) : (
                <ul class="space-y-2">
                  {upcomingRuns.map((workflow) => (
                    <li
                      key={workflow.workflowId}
                      class="flex items-center justify-between p-2 rounded hover:bg-base-300 cursor-pointer transition-colors"
                      onClick={() => handleWorkflowClick(workflow.workflowId)}
                    >
                      <div class="flex items-center gap-2 min-w-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4 flex-shrink-0 text-primary"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        <span class="truncate text-sm">{workflow.name}</span>
                      </div>
                      <div class="flex items-center gap-2 flex-shrink-0">
                        <span class="text-xs text-base-content/60">
                          {formatRelativeTime(workflow.nextRun!)}
                        </span>
                        <button
                          class="btn btn-ghost btn-xs"
                          onClick={(e) => handleRunNow(workflow.workflowId, e)}
                          title="Run now"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                            />
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div class="card bg-base-100 border border-base-300">
            <div class="card-body p-4">
              <h2 class="card-title text-base">Recent Activity</h2>
              {recentExecutions.length === 0 ? (
                <p class="text-base-content/60 text-sm">No recent executions</p>
              ) : (
                <ul class="space-y-2">
                  {recentExecutions.slice(0, 5).map((execution, idx) => (
                    <li
                      key={`${execution.workflowId}-${execution.scheduledFor}-${idx}`}
                      class="flex items-center justify-between p-2 rounded hover:bg-base-300 cursor-pointer transition-colors"
                      onClick={() => {
                        if (execution.threadId) {
                          route(`${scope.getNavigationPrefix()}/workflows/${agentId}/${execution.workflowId}/${execution.threadId}`);
                        } else {
                          handleWorkflowClick(execution.workflowId);
                        }
                      }}
                    >
                      <div class="flex items-center gap-2 min-w-0">
                        <span class={`font-mono ${getStatusColor(execution.status)}`}>
                          {getStatusIcon(execution.status)}
                        </span>
                        <span class="truncate text-sm">{execution.workflowName}</span>
                      </div>
                      <span class="text-xs text-base-content/60 flex-shrink-0">
                        {formatRelativeTime(execution.executedAt || execution.scheduledFor)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* All Workflows List */}
        <div class="card bg-base-100 border border-base-300">
          <div class="card-body p-4">
            <h2 class="card-title text-base">All Workflows</h2>
            <div class="overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Schedule</th>
                    <th>Last Run</th>
                    <th>Next Run</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {workflows.map((workflow) => (
                    <tr
                      key={workflow.workflowId}
                      class="hover cursor-pointer"
                      onClick={() => handleWorkflowClick(workflow.workflowId)}
                    >
                      <td class="font-medium">{workflow.name}</td>
                      <td>
                        <WorkflowStatusBadge status={workflow.status} />
                      </td>
                      <td class="text-sm text-base-content/70">
                        {workflow.schedule.type === 'cron'
                          ? workflow.schedule.expression
                          : workflow.schedule.type}
                      </td>
                      <td class="text-sm text-base-content/70">
                        {workflow.lastRun ? (
                          <span class={getStatusColor(workflow.lastRun.status)}>
                            {getStatusIcon(workflow.lastRun.status)}{' '}
                            {formatRelativeTime(workflow.lastRun.timestamp)}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td class="text-sm text-base-content/70">
                        {workflow.nextRun ? formatRelativeTime(workflow.nextRun) : '—'}
                      </td>
                      <td>
                        <button
                          class="btn btn-ghost btn-xs"
                          onClick={(e) => handleRunNow(workflow.workflowId, e)}
                          title="Run now"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
